import { LiveDataSet } from "../../API/LiveData/LiveDataSet";
import { ILiveDataListener, ILiveDataListenerContext } from "../../ILiveDataListener";
import { MainSite, ITimeZoneData } from "../../MainSite";
import { ProfileComponent } from "../../ValueTypes/Profile";
import { DateRangePicker } from "../Helpers/DateRangePicker";
import { ISiteObservationsPage } from "./ISiteObservationsPage";

export interface ChartDataPoint {
    timestamp: number; //: x axis value..
    y: number;
    val: number;
}

interface ChartMeasure {
    min: number;
    max: number;
    mid: number;
    gapMin: number;
    gapMax: number;
}

interface ChartMeasures {
    x: ChartMeasure;
    y: ChartMeasure;
    val: ChartMeasure;
}

//: https://www.highcharts.com/demo/highcharts/heatmap-canvas
//: https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/highcharts/demo/heatmap-canvas
//: https://www.highcharts.com/demo/maps/heatmap

//: TODO: handle timezone in chart datetime...

export class HovmollerChart implements ILiveDataListener {
    public latestData: LiveDataSet | null = null;

    liveDataChanged(latestData: LiveDataSet, ctx: ILiveDataListenerContext): void {
        this.latestData = latestData;

        if (this.liveDataChangedCallback) {
            this.liveDataChangedCallback(latestData, this);
        }
    }

    // chart options to be applied (or overwritten) for all plots
    public static chartOptionsDefault(): __Highcharts.Options {
        return {
            chart: {
                type: 'heatmap',
                zoomType: 'y',
                resetZoomButton: {
                    position: {
                        align: 'left',
                        x: 1,
                        y: 1
                    },
                    relativeTo: 'plotBox'
                }
            },
            xAxis: {
                type: 'datetime',
                gridZIndex: 4,
            },
            yAxis: {
                gridZIndex: 4,
            },
            colorAxis: {
                startOnTick: false,
                endOnTick: false,
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: true
            },
            title: {
                text: ''
            },
        };
    }

    private $chartContainerSpeed: JQuery;
    private $chartContainerDirection: JQuery;
    private $chartColorAxisLabelSpeed: JQuery;
    private $chartColorAxisLabelDirection: JQuery;
    private chartSpeed: __Highcharts.ChartObject;
    private chartDirection: __Highcharts.ChartObject;
    private stationId: string;
    private $box: JQuery;
    private selectedHours: number;
    private forecast: boolean;
    private drp: DateRangePicker;
    private predefinedPeriods: number[] = [-24, -168, -720]; //: negative = past, positives = forecasts

    private reciprocalYAxisToggle: JQuery;

    private originalContainerHeightPercent: number;

    constructor(
        private mainSite: MainSite,
        page: ISiteObservationsPage,
        $container: JQuery,
        top: number,
        left: number,
        height: number,
        width: number,
        // chart options
        private chartOptionsSpeed: __Highcharts.Options,
        private chartOptionsDirection: __Highcharts.Options,
        //: intent: ability to make data retrieval a provided method so this chart can be flexible.. the callback will be responsible for handling appropriately formed data once retrieved and transformed.
        private getDataMethod: (tsFrom: moment.Moment, tsTo: moment.Moment, isSpeed: boolean, extractProfileCmpValue: (cp: ProfileComponent) => number, chart: HovmollerChart, callback: (data: ChartDataPoint[], isFailed: boolean, failedMessage?: string) => void) => void,
        //: intent: added this to help data fetching methods use livedata if necessary (e.g. to determine sensors/params IDs..)
        private liveDataChangedCallback?: (latestData: LiveDataSet, chart: HovmollerChart) => void,
        private reciprocalYAxisLabel?: string
    ) {
        this.stationId = page.station.Station.Id;

        this.$box = $('<div class="midi_SiteObservations_chart_container"></div>')
            .css('position', 'absolute')
            .css('height', height + '%')
            .css('width', width+ '%')
            .css('paddingTop', 5);

        $container.empty();
        $container.append(this.$box);
        let $chartToolbars = $('<div class="current_chart_selector no-select row" style="font-size: 0.9em;align-items: center;display: flex;"></div>').appendTo(this.$box);

        let $dateRangeSelector = $('<div class="col-sm-9" data-toolbar-type="dates"></div>')
            .appendTo($chartToolbars);

        //: add DateRangePicker:
        this.drp = new DateRangePicker(this.mainSite, $dateRangeSelector, (drp) => {
            if (!drp.onChangeDateViaSetValuesMethods) {
                this.drp.dropdownSetButtonText("Custom range");
                this.selectedHours = null; //: important: we use this to determine if livedata should shift datepicker range..
            }

            let data = drp.getData();

            if (data && data.isValid && data.hasRange) {
                this.updateChart();
            }
        });
        this.drp.render(null, "row", true);

        if (this.reciprocalYAxisLabel) {
            let box = $('<div style="display:flex;align-items:center;"></div>')
                .append($('<span style="font-size: 0.8em; margin-right: 0.3em;"></span>')
                    .text(this.reciprocalYAxisLabel))
                .appendTo($chartToolbars);

            this.reciprocalYAxisToggle = $('<input type="checkbox" style="margin-top: 0"/>')
                .click(() => {
                    MainSite.localStorageSet('reciprocalHovmollerYAxis', this.reciprocalYAxisToggle.is(':checked').toString());
                    this.updateChart();
                })
                .appendTo(box);

            //note that if the reciprocalYAxisLabel is set (currently only for waves) then this will apply to
            //all charts (we do not currently separate into different Hovmollers)
            let isChecked = (MainSite.localStorageGet('reciprocalHovmollerYAxis', null)) === "true";
            if (isChecked) {
                this.reciprocalYAxisToggle.click();
            }
        }

        // add drop-down with predefined data ranges
        this.predefinedPeriods.forEach((hours, i) => {
            let prev = i == 0 ? null : this.predefinedPeriods[i - 1];
            if (i == 0) {
                this.drp.dropdownAddItem('header', 'Predefined periods', null);
                if (hours < 0) {
                    this.drp.dropdownAddItem('separator', null, null);
                    this.drp.dropdownAddItem('header', 'Past', null);
                }
            }

            if (prev < 0 && hours >= 0) {
                this.drp.dropdownAddItem('separator', null, null);
                this.drp.dropdownAddItem('header', 'Forecast', null);
            }

            let isForecast: boolean = hours > 0;
            let days: number = hours / 24;
            let label: string = `${Math.abs(days)} day${(days == 1 || days == -1) ? '' : 's'}`;
            this.drp.dropdownAddItem(`predefined-hours-${hours}`, label, (drp) => {
                this.selectedHours = Math.abs(hours);
                this.forecast = isForecast;
                this.updateChartHours(Math.abs(hours), true, isForecast);

                let $el = $('<span>');
                $el.append($(`<em class="text-primary">${isForecast ? 'Forecast' : 'Past'}</em>`).css({ marginRight: 5 }));
                $el.append($(`<span>${label}</span>`));
                this.drp.dropdownSetButtonElements($el);
            }, i == 0 ? "active" : "", isForecast ? "fa-forward" : "fa-backward");
        });
        this.drp.dropdownSetButtonText("Select range");

        //: add a timezone indicator (tucked away in drop-down of predefined ranges)
        let tzd = this.drp.getSelectedTimezoneData();
        if (tzd) {
            if (this.predefinedPeriods.length > 0) {
                this.drp.dropdownAddItem('separator', null, null);
            }

            this.drp.dropdownAddItem('timezone-display', `${tzd.timeZoneCode} (${tzd.offsetString})`, null, "disabled", "fa-globe");
        }

        /*let originalContainerHeightPercent = 83;*/
        let originalContainerHeightPercent = 50;

        //// Speed
        // create div for container and append to box
        this.$chartContainerSpeed = $('<div class="hovmoller-chart-container" id="hovmoller-speed"></div>')
            .css('height', originalContainerHeightPercent + '%')
            .appendTo(this.$box);
        // set css
        this.$chartContainerSpeed
            .css({ marginTop: 5 });
        // create color axis label and append to box
        this.$chartColorAxisLabelSpeed = $('<div class="hovmoller-chart-label" style="position: relative; margin-top: -23px; font-size: 12px; text-align: center;"></div>').appendTo(this.$box);

        //// Direction
        if (this.chartOptionsDirection) {
            // create div for container and append to box
            this.$chartContainerDirection = $('<div class="hovmoller-chart-container" id="hovmoller-direction"></div>')
                .css('height', originalContainerHeightPercent + '%')
                .appendTo(this.$box);
            // set css
            this.$chartContainerDirection
                .css({ marginTop: 5 });
            // create color axis label and append to box
            this.$chartColorAxisLabelDirection = $('<div class="hovmoller-chart-label" style="position: relative; margin-top: -23px; font-size: 12px; text-align: center;"></div>').appendTo(this.$box);
        };

        //// Add charts
        //copy options here
        this.chartSpeed = this.addChart(this.$chartContainerSpeed, JSON.parse(JSON.stringify(this.chartOptionsSpeed)));
        if (this.chartOptionsDirection) {
            this.chartDirection = this.addChart(this.$chartContainerDirection, this.chartOptionsDirection);
        };

        // In the drop down menu for predefined time ranges, select the first option
        this.drp.dropdownTriggerItemClick(`predefined-hours-${this.predefinedPeriods[0]}`);

        if (this.mainSite.liveDataManager.latestData) {
            this.liveDataChanged(this.mainSite.liveDataManager.latestData, null);
        }
    }

    private updateChartHours(hours: number, reload: boolean, forecast: boolean): void {
        let toMoment: moment.Moment;
        let fromMoment: moment.Moment;
        let tzd: ITimeZoneData = this.drp.getSelectedTimezoneData();
        let tzn: string = 'UTC';
        if (tzd) {
            tzn = tzd.timeZoneName;
        }

        if (forecast) {
            fromMoment = moment.utc().tz(tzn);
            toMoment = moment.utc().tz(tzn).add(hours, 'h');
        } else {
            toMoment = moment.utc().tz(tzn);
            fromMoment = moment.utc().tz(tzn).subtract(hours, 'h');
        }

        this.drp.setValues(fromMoment, toMoment, true, "Hovmoeller.updateChartHours");
    }

    private addChart(chartContainer, chartOptions): __Highcharts.ChartObject {

        try {
            chartContainer.highcharts(chartOptions);
            let chart = chartContainer.highcharts();
            return chart
        } catch (err) {
            console.warn('unable to setup highchart: ', err);
            let chart = chartContainer.html('<div class="alert alert-danger">Error while trying to load chart.</div>');
            return chart
        }
    }

    private prepareChartSeries(data: ChartDataPoint[], measures: ChartMeasures, chartOptions): __Highcharts.SeriesOptions {
        let res = <__Highcharts.SeriesOptions>{ //: NB: having to set <any> as we're using some options provided by added modules (boost...), which are not defined in TS defs.
            boostThreshold: 100,
            //borderWidth: 0,
            nullColor: '#EFEFEF',
            colsize: 24 * 36e5, // one day // 36e5 ms in one hour
            //colsize: 600000,
            //rowsize: 20,
            tooltip: chartOptions.tooltip,
            turboThreshold: Number.MAX_VALUE, // #3404, remove after 4.0.5 release

            id: "HOVMOL",
            data: this.chartDataPointsToArrays(data),

            connectEnds: true,
            connectNulls: true,
            interpolation: true,
        };

        try {
            if (measures.x.gapMin && measures.x.gapMin > 0) {
                res['colsize'] = measures.x.gapMin;
            }
            if (measures.y.gapMax && measures.y.gapMax > 0) {
                res['rowsize'] = measures.y.gapMax;
            }
        } catch (err) {
            console.warn('HovmollerChart - prepareChartSeries ERROR: unable to apply measures to colsize & rowsize to series!', { err: err });
        }

        return res;
    }

    /**
     * Converts formatted data points (`ChartDataPoint`) to arrays for use in the chart data, as chart data does not know how to read objects necessarily.
     * @param data
     * @returns
     */
    private chartDataPointsToArrays(data: ChartDataPoint[]): any[] {
        let res = [];

        for (let i = 0; i < data.length; i++) {
            res.push([data[i].timestamp, data[i].y, data[i].val]);
        }
        return res;
    }

    /**
     * Helper to get data 'measures', usually min/max/mid points for y/val arrays.
     * @param data
     * @returns
     */
    private chartDataMeasures(data: ChartDataPoint[]): ChartMeasures {
        let measures: ChartMeasures = {
            x: {
                min: -Infinity,
                max: Infinity,
                mid: null,
                gapMin: null,
                gapMax: null,
            },
            y: {
                min: -Infinity,
                max: Infinity,
                mid: null,
                gapMin: null,
                gapMax: null,
            },
            val: {
                min: -Infinity,
                max: Infinity,
                mid: null,
                gapMin: null,
                gapMax: null,
            }
        };

        let uniquesY = [];
        let uniquesX = [];

        for (let i = 0; i < data.length; i++) {
            let x = data[i];
            if (this.reciprocalYAxisToggle && this.reciprocalYAxisToggle.is(':checked')) {
                x.y = 1 / x.y;
            }

            if (x.timestamp && uniquesX.indexOf(x.timestamp) < 0) {
                uniquesX.push(x.timestamp);
            }
            if (x.y && uniquesY.indexOf(x.y) < 0) {
                uniquesY.push(x.y);
            }

            if (measures.x.min == -Infinity || measures.x.min > x.timestamp) {
                measures.x.min = x.timestamp;
            }
            if (measures.x.max == Infinity || measures.x.max < x.timestamp) {
                measures.x.max = x.timestamp;
            }

            if (measures.y.min == -Infinity || measures.y.min > x.y) {
                measures.y.min = x.y;
            }
            if (measures.y.max == Infinity || measures.y.max < x.y) {
                measures.y.max = x.y;
            }

            if (measures.val.min == -Infinity || measures.val.min > x.val) {
                measures.val.min = x.val;
            }
            if (measures.val.max == Infinity || measures.val.max < x.val) {
                measures.val.max = x.val;
            }
        }

        uniquesX.sort((a, b) => a - b);
        uniquesY.sort((a, b) => a - b);
        console.info('hovmol chart, data measures, uniques:', { x: uniquesX, y: uniquesY });

        let solveGapMinMax = (arr: number[]): { min?: number, max?: number } => {
            let gapMin = null;
            let gapMax = null;

            if (arr.length > 1) {
                for (let i = 1; i < arr.length; i++) {
                    let current = arr[i];
                    let previous = arr[i - 1];
                    let gap = Math.abs(current - previous);
                    if (gapMin == null || gapMin > gap) {
                        gapMin = gap;
                    }
                    if (gapMax == null || gapMax < gap) {
                        gapMax = gap;
                    }
                }
            }

            return { min: gapMin, max: gapMax };
        };

        let solveX = solveGapMinMax(uniquesX);
        let solveY = solveGapMinMax(uniquesY);

        measures.y.gapMin = solveY.min;
        measures.y.gapMax = solveY.max;
        measures.x.gapMin = solveX.min;
        measures.x.gapMax = solveX.max;

        measures.y.mid = (measures.y.min + measures.y.max) / 2;
        measures.val.mid = (measures.val.min + measures.val.max) / 2;

        return measures;
    }

    private applyChartData(data: ChartDataPoint[], chart, chartOptions, chartColorAxisLabel): void {

        // when waiting for data
        if (!data.length) {
            chart.showLoading('Data not available');
            return;
        }

        // prepare data for plotting
        let measures: ChartMeasures = this.chartDataMeasures(data);
        let series = this.prepareChartSeries(data, measures, chartOptions);

        // if rowsize is set in series, use it
        let rowsize = null;
        if (series && 'rowsize' in series) {
            rowsize = series['rowsize'];
        }

        // set y-axis limits
        if (rowsize && !isNaN(rowsize)) {
            chartOptions.yAxis = {
                ...chartOptions.yAxis,
                min: measures.y.min + Math.abs(rowsize / 2),
                max: measures.y.max - Math.abs(rowsize / 2),
            };
        }

        // Speed: setting color axis label
        try {
            chartColorAxisLabel.text(series.tooltip.headerFormat.replace('<br>', '').replace('<br/>', '')); //: hack, but idea is to show chart's values title/units below colorAxis..
        } catch (err) {
            chartColorAxisLabel.text('');
        }

        // update the chart
        chart.update(<any>chartOptions, false);

        // not sure. if there is more than one chart, remove the first chart?
        if (chart.series.length >= 1) {
            chart.series[0].remove();
        }
        chart.addSeries(series);

        console.info("HOVMOL CHART (DATA received & options/series prepared)", { instance: this, data: data, measures: measures, series: series, chartOptions: chartOptions });
        chart.hideLoading();
        chart.reflow();
    }

    public updateChart(): void {
        let range = this.drp.getData();

        if (range && range.isValid && range.hasRange) { //  && this.mainSite.liveDataManager.latestData

            // Speed: Get data and apply to chart
            this.chartSpeed.showLoading('Getting data...');
            this.getDataMethod(range.from, range.to, true, cmp => cmp.Speed, this, (data, failed, msg) => {
                if (failed) {
                    this.chartSpeed.showLoading(msg ?? 'Error while retrieving chart data.');
                } else {
                    let opts = JSON.parse(JSON.stringify(this.chartOptionsSpeed));
                    if (this.reciprocalYAxisToggle && this.reciprocalYAxisToggle.is(':checked') && this.reciprocalYAxisLabel) {
                        opts.yAxis["title"]["text"] = this.reciprocalYAxisLabel;
                    }

                    this.applyChartData(data, this.chartSpeed, opts, this.$chartColorAxisLabelSpeed);
                }
            });

            if (this.chartOptionsDirection) {
                // Direction: Get data and apply to chart
                this.chartDirection.showLoading('Getting data...');
                this.getDataMethod(range.from, range.to, false, cmp => cmp.Direction, this, (data, failed, msg) => {
                    if (failed) {
                        this.chartDirection.showLoading(msg ?? 'Error while retrieving chart data.');
                    } else {
                        this.applyChartData(data, this.chartDirection, this.chartOptionsDirection, this.$chartColorAxisLabelDirection);
                    }
                });
            };
        }
    }
}